import { useState, useEffect, useRef } from 'react';
import { SortingState, OnChangeFn } from '@tanstack/react-table';

interface UseTablePaginationProps {
  data: any[];
  initialSorting?: SortingState;
  pageSize?: number;
  preservePageOnDataChange?: boolean;
  tableId?: string;
}

interface UseTablePaginationReturn {
  sorting: SortingState;
  setSorting: OnChangeFn<SortingState>;
  pageIndex: number;
  setPageIndex: (index: number) => void;
  currentPageSize: number;
  setCurrentPageSize: (size: number) => void;
  globalFilter: string;
  setGlobalFilter: (filter: string) => void;
  filterChangeRef: React.RefObject<boolean>;
  paginationConfig: {
    autoResetPageIndex: boolean;
    state: {
      sorting: SortingState;
      pagination: {
        pageSize: number;
        pageIndex: number;
      };
      globalFilter: string;
    };
    onSortingChange: OnChangeFn<SortingState>;
    onPaginationChange: (updater: any) => void;
    onGlobalFilterChange: (filter: string) => void;
  };
}

export function useTablePagination({
  data,
  initialSorting = [],
  pageSize = 10,
  preservePageOnDataChange = false,
  tableId = 'default'
}: UseTablePaginationProps): UseTablePaginationReturn {
  const storageKey = `tablePageSize_${tableId}`;
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [pageIndex, setPageIndex] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved ? Number(saved) : pageSize;
  });
  const [globalFilter, setGlobalFilter] = useState('');
  
  const dataLengthRef = useRef(data.length);
  const dataRefObject = useRef(data);
  const filterChangeRef = useRef(false);

  useEffect(() => {
    localStorage.setItem(storageKey, currentPageSize.toString());
  }, [currentPageSize, storageKey]);
  
  useEffect(() => {
    filterChangeRef.current = true;
    // Short delay to allow filter to update
    const timeout = setTimeout(() => {
      filterChangeRef.current = false;
    }, 100);
    return () => clearTimeout(timeout);
  }, [globalFilter]);
  
  // Handle data changes and pagination
  useEffect(() => {
    const isDataSourceChange = 
      // Different reference (complete data replacement)
      dataRefObject.current !== data && 
      // Not triggered by a filter change
      !filterChangeRef.current;
    
    const isDataLengthChange = dataLengthRef.current !== data.length;
    
    // Calculate new page count
    const newPageCount = Math.ceil(data.length / currentPageSize) || 1;
    
    // Manage pagination state based on data changes
    if (isDataLengthChange) {
      if (preservePageOnDataChange) {
        // Only adjust if current page is out of bounds
        if (pageIndex >= newPageCount) {
          setPageIndex(Math.max(0, newPageCount - 1));
        }
      } else if (isDataSourceChange) {
        // Only reset page when data source changes (not just filtering)
        setPageIndex(0);
      }
    }
    
    dataLengthRef.current = data.length;
    dataRefObject.current = data;
  }, [data, currentPageSize, pageIndex, preservePageOnDataChange]);

  // Create a properly typed setSorting function that conforms to OnChangeFn<SortingState>
  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting(updater instanceof Function ? updater(sorting) : updater);
  };

  const paginationConfig = {
    autoResetPageIndex: false, // Prevent auto page reset on data changes
    state: {
      sorting,
      pagination: {
        pageSize: currentPageSize,
        pageIndex,
      },
      globalFilter,
    },
    onSortingChange: handleSortingChange,
    onPaginationChange: (updater: any) => {
      if (typeof updater === 'function') {
        const newState = updater({
          pageIndex,
          pageSize: currentPageSize,
        });
        setPageIndex(newState.pageIndex);
        setCurrentPageSize(newState.pageSize);
      } else {
        setPageIndex(updater.pageIndex);
        setCurrentPageSize(updater.pageSize);
      }
    },
    onGlobalFilterChange: setGlobalFilter,
  };

  return {
    sorting,
    setSorting: handleSortingChange,
    pageIndex,
    setPageIndex,
    currentPageSize,
    setCurrentPageSize,
    globalFilter,
    setGlobalFilter,
    filterChangeRef,
    paginationConfig
  };
} 